import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { ChevronDown, ChevronUp, Mail, Info } from 'lucide-react';
import { formatCurrency, getLenderRateRange, formatPercentage } from './financingCalculations';

export default function LenderDetailsPanel({ lender, onExpressInterest, className = '' }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);

  if (!lender) return null;

  const handleConfirmInterest = () => {
    setShowInterestModal(false);
    if (onExpressInterest) {
      onExpressInterest(lender.id);
    } else {
      const returnTo = encodeURIComponent('/succession/financing');
      window.location.href = `/loans/interested_in_financing?lender_id=${lender.id}&return_to=${returnTo}`;
    }
  };

  const bands = lender.rateBands || lender.rate_bands || [];
  const baseRate = lender.roi || 0;
  const logoUrl = lender.smallLogoUrl || lender.small_logo_url || lender.largeLogoUrl || lender.large_logo_url;
  const minLoan = lender.minimumLoanAmount || lender.minimum_loan_amount || lender.loanAmountMinimum || lender.loan_amount_minimum;
  const maxLoan = lender.maximumLoanAmount || lender.maximum_loan_amount || lender.loanAmountMaximum || lender.loan_amount_maximum;
  const feesInfo = lender.feesInfo || lender.fees_info;
  const structureInfo = lender.structureInfo || lender.structure_info;
  const prepaymentInfo = lender.prepaymentInfo || lender.prepayment_info;
  const specialTerms = lender.otherConsiderationsInfo || lender.other_considerations_info;
  const contactName = lender.contactName || lender.contact_name;
  const contactEmail = lender.contactEmail || lender.contact_email;

  return (
    <React.Fragment>
      <div className={`bg-succession-bg-primary border border-succession-border rounded-xl shadow-succession overflow-hidden transition-colors duration-300 ${className}`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-6 flex items-center justify-between hover:bg-succession-bg-secondary transition-colors duration-300"
        >
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={lender.name}
                className="w-12 h-12 rounded-full border-2 border-succession-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-succession-bg-secondary flex items-center justify-center text-succession-primary font-bold text-lg">
                {(lender.name || '?')[0]}
              </div>
            )}
            <div className="text-left">
              <h3 className="text-lg font-semibold text-succession-text-primary">{lender.name}</h3>
              <p className="text-sm text-succession-text-secondary">Click to view lender details</p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-succession-text-secondary" />
          ) : (
            <ChevronDown className="w-5 h-5 text-succession-text-secondary" />
          )}
        </button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-succession-border"
            >
              <div className="p-6 space-y-6">
                {/* Description */}
                {lender.description && (
                  <div>
                    <h4 className="text-sm font-semibold text-succession-text-primary mb-2">
                      About {lender.name}
                    </h4>
                    <p className="text-sm text-succession-text-secondary">{lender.description}</p>
                  </div>
                )}

                {/* Loan Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-succession-text-secondary mb-1">Loan Amount Range</div>
                    <div className="text-sm font-semibold">
                      {formatCurrency(minLoan || 0)} - {formatCurrency(maxLoan || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-succession-text-secondary mb-1">Interest Rate Range</div>
                    <div className="text-sm font-semibold">{getLenderRateRange(lender)}</div>
                  </div>
                </div>

                {/* Rate Structure */}
                {bands.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-succession-text-primary mb-3">Rate Structure</h4>
                    <div className="space-y-2">
                      {bands.map((band, index) => {
                        const lower = band.loanAmountLowerBand || band.loan_amount_lower_band;
                        const upper = band.loanAmountUpperBand || band.loan_amount_upper_band;
                        const variable = band.variableInterestRate || band.variable_interest_rate || 0;
                        const totalRate = baseRate + variable;
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-succession-bg-secondary rounded-lg"
                          >
                            <span className="text-sm text-succession-text-secondary">
                              {formatCurrency(lower)} - {formatCurrency(upper)}
                            </span>
                            <Badge variant="secondary">{formatPercentage(totalRate)}</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Fees & Terms */}
                {(feesInfo || structureInfo || prepaymentInfo) && (
                  <div>
                    <h4 className="text-sm font-semibold text-succession-text-primary mb-3">Fees &amp; Terms</h4>
                    <div className="space-y-2 text-sm">
                      {feesInfo && (
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-succession-text-secondary mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-succession-text-secondary">Fees: </span>
                            <span className="font-medium">{feesInfo}</span>
                          </div>
                        </div>
                      )}
                      {structureInfo && (
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-succession-text-secondary mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-succession-text-secondary">Structure: </span>
                            <span className="font-medium">{structureInfo}</span>
                          </div>
                        </div>
                      )}
                      {prepaymentInfo && (
                        <div className="flex items-start gap-2">
                          <Info className="w-4 h-4 text-succession-text-secondary mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="text-succession-text-secondary">Prepayment: </span>
                            <span className="font-medium">{prepaymentInfo}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Special Terms */}
                {specialTerms && (
                  <div className="p-4 bg-succession-bg-secondary border border-succession-border rounded-lg">
                    <h4 className="text-sm font-semibold text-succession-primary mb-2">Special Terms</h4>
                    <p className="text-sm text-succession-text-primary">{specialTerms}</p>
                  </div>
                )}

                {/* Contact Info */}
                {(contactName || contactEmail) && (
                  <div className="pt-4 border-t border-succession-border">
                    <h4 className="text-sm font-semibold text-succession-text-primary mb-3">
                      Contact Information
                    </h4>
                    <div className="space-y-2">
                      {contactName && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-succession-text-secondary" />
                          <span className="text-succession-text-secondary">Contact: </span>
                          <span className="font-medium">{contactName}</span>
                        </div>
                      )}
                      {contactEmail && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-succession-text-secondary" />
                          <a
                            href={`mailto:${contactEmail}`}
                            className="text-succession-primary hover:underline"
                          >
                            {contactEmail}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <Button
                  onClick={() => setShowInterestModal(true)}
                  className="w-full"
                  size="lg"
                >
                  Express Interest in {lender.name}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Express Interest Modal */}
      <Dialog open={showInterestModal} onOpenChange={setShowInterestModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Express Interest in Financing</DialogTitle>
            <DialogDescription>
              You&apos;re about to express interest in financing with{' '}
              <strong>{lender.name}</strong>. We&apos;ll notify the lender and connect you via
              email.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <div className="flex items-center gap-3 p-3 bg-succession-bg-secondary rounded-lg">
              {logoUrl ? (
                <img src={logoUrl} alt={lender.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-succession-bg-primary border border-succession-border flex items-center justify-center text-succession-primary font-bold">
                  {(lender.name || '?')[0]}
                </div>
              )}
              <div>
                <div className="font-semibold">{lender.name}</div>
                {contactName && (
                  <div className="text-sm text-succession-text-secondary">{contactName} will reach out to you</div>
                )}
              </div>
            </div>

            <p className="text-sm text-succession-text-secondary">
              By expressing interest, you agree to share your contact information and loan
              requirements with the lender.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInterestModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmInterest}>
              Confirm &amp; Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
